import { useMemo, useState, type ReactNode } from "react";
import { Button, Flex, FullscreenTakeover, LoadingIndicator, Text } from "@jsq/ds/core";
import { useDateFormatterWithTimezone } from "~/pages/UserAndRoles/utils/dateFormatter";
import { SPACING } from "~/styles/variables";
import * as styles from "./styles_vanilla.css";

export type VersionData<T = unknown> = {
  id: number;
  version: number;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string;
  data: T;
};

type GenericVersionHistoryTakeoverProps<T> = {
  title: string;
  versions: VersionData<T>[];
  loading: boolean;
  onExit: () => void;
  renderPreview: (version: VersionData<T>) => ReactNode;
  noDataMessage?: string;
};

export const GenericVersionHistoryTakeover = <T,>({
  title,
  versions,
  loading,
  onExit,
  renderPreview,
  noDataMessage = "No data found",
}: GenericVersionHistoryTakeoverProps<T>) => {
  const { formatDateWithTimezone } = useDateFormatterWithTimezone();
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);

  // Derive the selected version from the versions array and selectedVersionId
  const selectedVersion = useMemo(() => {
    if (selectedVersionId !== null) {
      return versions.find((v) => v.id === selectedVersionId) ?? null;
    }
    // Default to the first version if none is selected
    return versions.length > 0 ? versions[0] : null;
  }, [versions, selectedVersionId]);

  return (
    <FullscreenTakeover>
      <FullscreenTakeover.Header
        title={title}
        leftActionContent={
          <Button variant="muted" icon="times" onClick={onExit}>
            Exit
          </Button>
        }
      />
      <FullscreenTakeover.Body className={styles.versionHistoryBody}>
        {loading ? (
          <Flex alignItems="center" justifyContent="center" flexGrow={1}>
            <LoadingIndicator />
          </Flex>
        ) : (
          <Flex className={styles.versionHistoryContainer}>
            {/* Left Panel - Version List */}
            <Flex flexDirection="column" className={styles.versionHistoryLeftPanel}>
              <Text variant="strong-md" className={styles.versionHistoryLeftPanelTitle}>
                Version history
              </Text>
              <Flex flexDirection="column" gap={SPACING.SMALL}>
                {versions.map((version) => {
                  const isSelected = selectedVersion?.id === version.id;
                  return (
                    <Flex
                      key={version.id}
                      flexDirection="column"
                      className={
                        isSelected
                          ? styles.versionHistoryItemSelected
                          : styles.versionHistoryItem
                      }
                      onClick={() => setSelectedVersionId(version.id)}
                    >
                      <Text variant={isSelected ? "strong-md" : "regular-md"}>
                        {version.version}.0
                      </Text>
                      <Text variant="secondary-sm">
                        {formatDateWithTimezone(version.createdAt, "MMM d, yyyy h:mm a", false)}
                        {version.createdBy ? ` by ${version.createdBy}` : ""}
                      </Text>
                    </Flex>
                  );
                })}
              </Flex>
            </Flex>

            {/* Right Panel - Preview */}
            <Flex
              flexDirection="column"
              flexGrow={1}
              className={styles.versionHistoryRightPanel}
            >
              {versions.length === 0 ? (
                <Flex alignItems="center" justifyContent="center" flexGrow={1}>
                  <Text variant="secondary-md">{noDataMessage}</Text>
                </Flex>
              ) : selectedVersion ? (
                renderPreview(selectedVersion)
              ) : (
                <Flex alignItems="center" justifyContent="center" flexGrow={1}>
                  <Text variant="secondary-md">Select a version to view details</Text>
                </Flex>
              )}
            </Flex>
          </Flex>
        )}
      </FullscreenTakeover.Body>
    </FullscreenTakeover>
  );
};
